import { useEffect, useRef } from "react";
import type {
  StoneFinding,
  StoneLaterality,
  StoneLocation,
  StoneVerdict,
} from "../../stones/types";
import { LOCATION_LABEL, LOCATION_OPTIONS } from "../../stones/labels";

const SELECT_CLASS =
  "appearance-none w-fit font-mono text-[11px] bg-surface-2 border border-border-strong rounded-[2px] px-1.5 py-1 text-fg-muted cursor-pointer focus:outline-none focus:border-border-strong";

export interface StoneCardProps {
  stone: StoneFinding; // already effectiveStone()-merged
  verdict: StoneVerdict; // "pending" by default
  selected: boolean;
  thumbnail: HTMLCanvasElement | null;
  onSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
  onLocationChange: (id: string, loc: StoneLocation) => void;
  onLateralityChange: (id: string, laterality: StoneLaterality) => void;
}

/** Presentational stone-finding card: thumbnail + measurements/HU + location
 * dropdown + approve/deny. No Jotai/atoms — all state comes in via props,
 * all mutation goes out via callbacks. The card root selects the finding;
 * the select/buttons stop-propagate so operating them doesn't also select. */
export function StoneCard({
  stone,
  verdict,
  selected,
  thumbnail,
  onSelect,
  onApprove,
  onDeny,
  onLocationChange,
  onLateralityChange,
}: StoneCardProps) {
  const thumbRef = useRef<HTMLDivElement>(null);

  // Thumbnail is a plain <canvas> handed down from a parent that owns the
  // render loop — mount it into our container rather than re-rendering it,
  // so the parent's canvas (and its GL/2D context) is reused, not recreated.
  useEffect(() => {
    const el = thumbRef.current;
    if (!el) return;
    el.innerHTML = "";
    if (thumbnail) el.appendChild(thumbnail);
  }, [thumbnail]);

  const approved = verdict === "approved";
  const denied = verdict === "denied";

  return (
    <div
      data-testid="stone-card"
      role="button"
      tabIndex={0}
      aria-label="Select stone finding"
      onClick={() => onSelect(stone.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(stone.id);
        }
      }}
      className={`flex gap-3 p-2.5 bg-surface border rounded-[2px] cursor-pointer transition-colors ${
        selected ? "border-signal" : "border-border hover:border-border-strong"
      } ${denied ? "opacity-50" : ""}`}
    >
      <div
        ref={thumbRef}
        className="shrink-0 w-[132px] h-[132px] rounded-[2px] bg-bg-2 overflow-hidden flex items-center justify-center"
      />

      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <select
          aria-label="Stone laterality"
          value={stone.laterality ?? ""}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onLateralityChange(stone.id, e.target.value as StoneLaterality)}
          className={SELECT_CLASS}
        >
          {stone.laterality == null && (
            <option value="" disabled>
              select side…
            </option>
          )}
          <option value="left">Left</option>
          <option value="right">Right</option>
        </select>

        <div className="flex items-start justify-between gap-2">
          <div className="font-mono text-xs text-fg">
            &#8960; {stone.measurements.longAxisMm.toFixed(1)} &times;{" "}
            {stone.measurements.shortAxisMm.toFixed(1)} mm
          </div>
          <span
            className="shrink-0 text-[9px] font-mono font-bold uppercase tracking-widest px-1.5 py-px rounded-[2px] border border-signal-stroke text-signal-text-tint bg-signal-tint"
            title="Model confidence"
          >
            {stone.confidence.toFixed(2)}
          </span>
        </div>

        <div className="font-mono text-[11px] text-fg-muted">
          HU {Math.round(stone.hu.max)}/{Math.round(stone.hu.mean)}
        </div>

        <select
          aria-label="Stone location"
          value={stone.location ?? ""}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onLocationChange(stone.id, e.target.value as StoneLocation)}
          className={SELECT_CLASS}
        >
          {stone.location == null && (
            <option value="" disabled>
              select location…
            </option>
          )}
          {LOCATION_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {LOCATION_LABEL[opt]}
            </option>
          ))}
        </select>

        <div className="flex gap-1.5 mt-auto pt-0.5">
          <button
            type="button"
            aria-pressed={approved}
            onClick={(e) => {
              e.stopPropagation();
              onApprove(stone.id);
            }}
            className={`h-6 px-2 rounded-[2px] border font-mono text-[10px] uppercase tracking-wide transition-colors ${
              approved
                ? "bg-signal text-white border-signal"
                : "border-border-strong text-fg-muted hover:text-fg hover:bg-surface-2"
            }`}
          >
            Approve
          </button>
          <button
            type="button"
            aria-pressed={denied}
            onClick={(e) => {
              e.stopPropagation();
              onDeny(stone.id);
            }}
            className={`h-6 px-2 rounded-[2px] border font-mono text-[10px] uppercase tracking-wide transition-colors ${
              denied
                ? "bg-warn-tint text-warn border-warn/50"
                : "border-border-strong text-fg-muted hover:text-fg hover:bg-surface-2"
            }`}
          >
            Deny
          </button>
        </div>
      </div>
    </div>
  );
}
